import { Language } from './types'
import { RootStoreState } from '../root/index'
import { getConfiguration } from '../configuration/index'
import { ProcessingManifestItem } from './actions'
import { ItemCompact, LanguageCode } from 'mytours-read-api-typescript'
import { ProcessingManifestState } from './reducer'

export function convertLanguages(serverLanguageCodes: string[]): Language[] {
	const languages: Language[] = getConfiguration().getLanguages()
	return serverLanguageCodes.map(language => {
		const found = languages.find(next => next.code === language)
		if (found) {
			return found
		}
		throw Error('Language not found: ' + language)
	})
}

export function convertLanguage(serverLanguageCode: string): Language {
	const languages: Language[] = getConfiguration().getLanguages()
	const language = languages.find(next => next.code === serverLanguageCode)
	if (language === undefined) {
		throw Error('Language not found: ' + serverLanguageCode)
	}
	return language
}

export function getCurrentLanguageCode(state: RootStoreState): string {
	const { selectedLanguage, appMetaData } = state.settings
	// if user has a selected language, use that
	if (selectedLanguage) {
		return selectedLanguage.code
	}
	// fall back to primary language
	if (appMetaData?.primary_language) {
		return appMetaData.primary_language
	}
	// this function should only be called AFTER the app meta data is available on redux so we should always be able to at least use the primary language code
	throw Error('Current language is unknown')
}

export function convertManifestItem(
	item: ItemCompact,
	language: LanguageCode,
	type: 'screen' | 'collection' | 'collectionItem' | 'media',
	state: 'processing' | 'success_downloaded' | 'success_existed' | 'failed',
): ProcessingManifestItem {
	return {
		itemId: item.id!,
		itemVersion: item.version!,
		language,
		type,
		state,
	}
}

export function updateManifestMap(
	existing: ProcessingManifestState,
	id: number,
	language: string,
	key: 'screen' | 'collection' | 'collectionItem' | 'media',
	state: 'processing' | 'success_downloaded' | 'success_existed' | 'failed',
): ProcessingManifestState {
	// create key
	const itemKey: string = id + language
	// clone manifest
	const newManifest: ProcessingManifestState = { ...existing }
	// find existing item
	const existingItem = newManifest[key].get(itemKey)
	if (existingItem) {
		// update item value
		const newValue: ProcessingManifestItem = { ...existingItem, state }
		newManifest[key].set(itemKey, newValue)
	}
	return newManifest
}

/**
 * returns the current processing manifest state
 */
export const manifestStateSelector = (
	state: RootStoreState,
): 'initialising' | 'failed' | 'processing' | 'finished' | undefined =>
	state.settings.processingManifest ? state.settings.processingManifest.state : undefined

/**
 * Helper method for creating an empty manifest state used by reducers
 * @param state the state to set on empty manifest object
 */
export function createEmptyManifest(
	state: 'initialising' | 'failed' | 'processing' | 'finished',
): ProcessingManifestState {
	return {
		state,
		screen: new Map(),
		collection: new Map(),
		collectionItem: new Map(),
		media: new Map(),
		allLanguages: [],
		completedLanguages: [],
	}
}
